import React, { FC } from "react"
import PropTypes from "prop-types"
import { ArrowLink } from "../views/atoms/links/ArrowLink"
import CarrotMarkdown from "../../utils/CarrotMarkdown"
import styled from "styled-components"
import { useSelector } from "react-redux"
import { getCompanyName } from "../../reducers/companyInfo"
import { TextLink } from "components/views/atoms/links/TextLink"
import useAvaCheckoutLink from "components/carrot-plans/hooks/useAvaCheckoutLink"
import { LoadingText } from "components/views/atoms/Atoms"
import { CalloutBox } from "components/views/CalloutBox"
import { ZendeskTextLink } from "../views/atoms/links/ZendeskTextLink"
import { ZendeskArrowLink } from "../views/atoms/links/ZendeskArrowLink"
import { isZendeskLink } from "carrot-api/HelpCenterClient"
import { PayingForCareCoverageDetails } from "../views/reimbursements/digital-benefit-guide/PayingForCare"
import { BenefitConfigurationMarkdown } from "./BenefitConfigurationMarkdown"
import { FlexContainer, Text } from "@carrotfertility/carotene"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import useBenefit from "./hooks/useGetBenefit"
import { PiiText } from "services/tracking"

const DynamicH2 = styled.h2`
  font-family: Publico Headline;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.8);
  margin-top: 0;
  margin-bottom: 4px;
`

const DynamicH3 = styled.h3`
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: rgba(0, 0, 0, 0.8);
  margin-top: 24px;
  margin-bottom: 0;
`

const DynamicH5 = styled.h5`
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicP = styled.p`
  margin-top: 0;
  margin-bottom: 24px;
  line-height: 150%;
  font-family: "Gotham A", "Gotham B", sans-serif;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicHr = styled.hr`
  margin: 32px 0 32px 0;
  border: none;
  border-bottom: rgba(0, 0, 0, 0.1) solid 1px;
`

const DynamicUl = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return (
    <ul>
      <FlexContainer direction="column" gap="12px" paddingLeftRight="small">
        {children}
      </FlexContainer>
    </ul>
  )
}

const DynamicOl = styled.ol`
  margin-left: -24px;
  font-weight: 500;
  padding-inline-start: 40px;
  list-style: decimal;
  li {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`

const DynamicLi = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return (
    <li>
      <Text as="span" size="medium" color="black-80" style={{ wordBreak: "break-word" }}>
        {children}
      </Text>
    </li>
  )
}

const OlBody = styled.p`
  margin-top: 0;
  margin-bottom: 12px;
  line-height: 150%;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicBodySmall = styled.p`
  // currently only used in paying for care
  // margin-top set to -20px to collapse margin under blockquote
  margin-top: -20px;
  margin-bottom: 14px;
  font-size: 14px;
  line-height: 150%;
  font-family: "Gotham A", "Gotham B", sans-serif;
  color: rgba(0, 0, 0, 0.6);
`

const BlockQuote = styled.blockquote`
  margin: 0;
`

const DynamicBlockQuoteWrapper = styled.div`
  padding: 24px;
  border-radius: 8px;
  width: 100%;
  background-color: #faf9f7;
  margin: 16px 0 24px 0;
  display: flex;
  p {
    font-family: "Gotham A", "Gotham B", sans-serif;
    margin-top: 0;
    margin-bottom: 0;
    font-size: 14px;
    line-height: 150%;
    color: rgba(0, 0, 0, 0.8);
    &:last-child {
      margin-bottom: 0;
    }
  }
`

const CalloutBoxAdapter = styled(CalloutBox)`
  ol,
  ul {
    margin-left: 14px;
  }

  * {
    margin-bottom: 0;
  }
`

export const DynamicBlockQuote = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <DynamicBlockQuoteWrapper>
    <BlockQuote>{children}</BlockQuote>
  </DynamicBlockQuoteWrapper>
)

const isInternalLink = (href: string): boolean => {
  // test if the start of the href is "/", "./" or "../" followed by a non "/" character
  if (!href) return false

  if (/^\.{0,2}\/[^/]/.test(href)) {
    return true
  }
  const url = new URL(href)
  return ["protocol", "host", "port"].every((part) => url[part] === window.location[part])
}

const dynamicContentStyleOverrides = {
  h2: DynamicH2,
  h3: DynamicH3,
  h5: DynamicH5,
  p: DynamicP,
  hr: DynamicHr,
  ul: DynamicUl,
  ol: DynamicOl,
  li: DynamicLi,
  OlBody: OlBody,
  BodySmall: DynamicBodySmall,
  CompanyName: () => {
    const companyNameInfo = useSelector(getCompanyName)
    return <PiiText>{companyNameInfo}</PiiText>
  },
  CoverageAmountsDbg: () => {
    return <PayingForCareCoverageDetails />
  },
  CustomCoverage: () => {
    const {
      data: {
        customizations: { memberCustomCoverageExplanationMarkdown }
      }
    } = useBenefit()

    return memberCustomCoverageExplanationMarkdown ? (
      <BenefitConfigurationMarkdown benefitConfigurationMarkdown={memberCustomCoverageExplanationMarkdown} />
    ) : null
  },
  AdditionalEmployerBenefits: () => {
    const {
      data: {
        customizations: { additionalEmployerBenefitMarkdown }
      }
    } = useBenefit()

    return additionalEmployerBenefitMarkdown ? (
      <BenefitConfigurationMarkdown benefitConfigurationMarkdown={additionalEmployerBenefitMarkdown} />
    ) : null
  },
  ArrowLink: ({ children, href }) => {
    if (!href) {
      reportErrorMessage(`Empty href found in Markdown with children ${JSON.stringify(children)}`)
    }
    if (isZendeskLink(href)) {
      return (
        <ZendeskArrowLink to={href} target="_blank">
          {children}
        </ZendeskArrowLink>
      )
    }

    return (
      <ArrowLink to={href} target="_blank">
        {children}
      </ArrowLink>
    )
  },
  AvaArrowLink: ({ children }) => {
    const { data: avaCheckoutLandingPageUrl, isLoading } = useAvaCheckoutLink()
    return (
      <div>
        {isLoading ? (
          <LoadingText />
        ) : (
          <ArrowLink to={avaCheckoutLandingPageUrl} target="_blank">
            {" "}
            {children}{" "}
          </ArrowLink>
        )}
      </div>
    )
  },
  blockquote: ({ children }) => <DynamicBlockQuote>{children}</DynamicBlockQuote>,
  a({ href, title, children }) {
    if (!href) {
      reportErrorMessage(`Empty href found in Markdown with title ${title} and children ${JSON.stringify(children)}`)
    }
    if (isZendeskLink(href)) {
      return (
        <ZendeskTextLink target="_blank" title={title} to={href}>
          {children}
        </ZendeskTextLink>
      )
    }
    const target = isInternalLink(href) ? "_self" : "_blank"
    return (
      <TextLink target={target} title={title} to={href}>
        {children}
      </TextLink>
    )
  },
  CalloutBox(props) {
    return <CalloutBoxAdapter {...props} />
  }
}

type DynamicContentMarkdownFields = {
  additionalClasses?: string
  styleOverrides?: object
  source: string
}

const DynamicContentMarkdown: FC<DynamicContentMarkdownFields> = ({
  additionalClasses = "",
  source,
  styleOverrides = dynamicContentStyleOverrides
}) => (
  <div className={additionalClasses}>
    <CarrotMarkdown source={source} styleOverrides={styleOverrides} />
  </div>
)

DynamicContentMarkdown.propTypes = {
  source: PropTypes.string.isRequired,
  additionalClasses: PropTypes.string
}

const getStyleOverridesClone = () => ({ ...dynamicContentStyleOverrides })

export { DynamicContentMarkdown, getStyleOverridesClone, isInternalLink }
